import { TypePill } from "@/components/TypePill";
import { TypePillRefs } from "@/components/TypePill/TypePill";
import { parseBreaksToParagraphs } from "@/core/utils";
import { useGSAP } from "@gsap/react";
import className from "classnames/bind";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { forwardRef, useImperativeHandle, useRef } from "react";
import styles from "./CantUnhearItHeader.module.scss";
import { usePostType } from "@/hooks/postType";
import { useClientMount } from "@/providers/ClientMountProvider";
import { useViewSize } from "@/providers/ViewSizeContext";

type CantUnhearItHeaderProps = {
  pillText: string;
  headlineMarkup: string;
  subhead: string;
};
const cx = className.bind(styles);

export interface CantUnhearItHeaderRefs {
  rootEl?: HTMLDivElement | null;
}

const CantUnhearIt = forwardRef<
  CantUnhearItHeaderRefs,
  CantUnhearItHeaderProps
>(({ pillText, headlineMarkup, subhead }, ref) => {
  const pillRef = useRef<TypePillRefs>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const subHeadRef = useRef<HTMLParagraphElement>(null);
  const headlineMarkupInLines = parseBreaksToParagraphs(headlineMarkup);
  const pillDefault = usePostType({ node: { __typename: "Post" } });
  const { mounted } = useClientMount();
  const { isReady } = useViewSize();

  useImperativeHandle(
    ref,
    () => ({
      rootEl: containerRef.current,
    }),
    [containerRef.current]
  );

  useGSAP(() => {
    if (!mounted || !isReady) return;
    if (pillRef.current && subHeadRef.current && titleRef.current) {
      gsap.registerPlugin(ScrollTrigger);
      gsap.fromTo(
        pillRef.current.rootEl,
        {
          opacity: 0,
        },
        { opacity: 1, duration: 0.4, ease: "linear" }
      );
      gsap.fromTo(
        titleRef.current.children,
        {
          y: "130%",
          willChange: "transform",
          clipPath: "rect(0% 100% 0% 0%)",
          skewY: 1,
          opacity: 1,
        },
        {
          y: "0%",
          ease: "power4.out",
          stagger: 0.1,
          willChange: "transform",
          clipPath: "rect(0% 100% 100% 0%)",
          duration: 1.4,
          skewY: 0,
          opacity: 1,
          onComplete: () => {
            Array.from(titleRef.current.children)
              .reverse()
              .forEach((el, index) => {
                gsap.to(el, {
                  y: `-${index * 120}%`,
                  scrollTrigger: {
                    scrub: 1,
                  },
                });
              });
          },
        }
      );
      gsap.fromTo(
        subHeadRef.current,
        {
          opacity: 0,
        },
        { opacity: 1, delay: titleRef.current.children.length * 0.1 + 0.4 }
      );
    }
  }, [mounted, isReady, pillRef, subHeadRef, titleRef]);

  return (
    <div className={cx("cant-unhear-it__header-container")} ref={containerRef}>
      <div className={cx("cant-unhear-it__header")}>
        { pillDefault && <TypePill type={pillDefault.key} copy={pillText || pillDefault.label} ref={pillRef} /> }
        <h1 className={cx("headline", "title-uc-xl")} ref={titleRef}>
          {headlineMarkupInLines.map((line, index) => (
            <span key={index} dangerouslySetInnerHTML={{ __html: line }} />
          ))}
        </h1>
        <p className={cx("subhead", "text-l")} ref={subHeadRef}>
          {subhead}
        </p>
      </div>
    </div>
  );
});

export default CantUnhearIt;
